/**
 * Whats New — list page. Mirror of Angular `VersionListComponent`.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Pencil, Trash2 } from 'lucide-react'
import { showSuccess, showApiError } from '@/lib/utils/notifications'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { ListPaginationBar } from '@/components/ui/ListPaginationBar'
import {
  ListingDataTableCard,
  LISTING_DATA_TABLE_CLASS,
} from '@/components/ui/listingDataTable'
import { PageShell } from '@/components/layout/PageShell'
import { ListingTableLoadingRow } from '@/components/ui/ModuleScreenLoading'
import { cn } from '@/lib/utils/helpers'
import { ChevronsUpDown } from 'lucide-react'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import parityStyles from '@/features/invitee/styles/invitesParity.module.css'
import configSortStyles from '@/components/listing/propertySortHeaderButton.module.css'
import { useUserRole } from '@/hooks/useUserRole'
import { WhatsNewEditModal } from '../components/WhatsNewEditModal'
import {
  deleteReleaseNote,
  getReleaseNotes,
  saveReleaseNote,
} from '../services/whatsNewService'
import type {
  ReleaseNote,
  ReleaseNoteListParams,
  ReleaseNoteSortKey,
  ReleaseNoteSubmitPayload,
  SortDirection,
} from '../types'
import { VERSION_TYPES } from '../types'

const DEFAULT_PARAMS: ReleaseNoteListParams = {
  page: 1,
  perPage: 15,
  orderBy: 'version_date',
  sortBy: 'desc',
}

const PER_PAGE_OPTIONS = [15, 30, 50, 100] as const
const PAGINATION_TEST_ID_PREFIX = 'whats-new'

function formatDate(value: string | undefined): string {
  if (!value) return '—'
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return value
  return `${match[2]}/${match[3]}/${match[1].slice(-2)}`
}

export function WhatsNewListPage() {
  const { roleName } = useUserRole()
  const isAdmin = roleName === 'ROLE_ADMIN'

  const [notes, setNotes] = useState<ReleaseNote[]>([])
  const [params, setParams] = useState<ReleaseNoteListParams>(DEFAULT_PARAMS)
  const [pagination, setPagination] = useState({ current: 1, last: 1, total: 0 })
  const [loading, setLoading] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<ReleaseNote | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<ReleaseNote | null>(null)

  const mountedRef = useRef(false)

  const load = useCallback(async (next: ReleaseNoteListParams) => {
    setLoading(true)
    try {
      const response = await getReleaseNotes(next)
      setNotes(response.data ?? [])
      setPagination({
        current: response.current ?? 1,
        last: response.last ?? 1,
        total: response.total ?? 0,
      })
    } catch (error) {
      showApiError(error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (mountedRef.current) return
    mountedRef.current = true
    void load(DEFAULT_PARAMS)
  }, [load])

  const applyParams = useCallback(
    (next: ReleaseNoteListParams) => {
      setParams(next)
      void load(next)
    },
    [load],
  )

  const toggleSort = (key: ReleaseNoteSortKey) => {
    const nextDir: SortDirection =
      params.orderBy === key && params.sortBy === 'asc' ? 'desc' : 'asc'
    applyParams({ ...params, orderBy: key, sortBy: nextDir, page: 1 })
  }

  const goToPage = (pageNo: number) => applyParams({ ...params, page: pageNo })

  const changePerPage = (perPage: number) => {
    applyParams({ ...params, perPage, page: 1 })
  }

  const lastPage = Math.max(1, pagination.last)
  const currentPage = Math.min(Math.max(1, pagination.current), lastPage)
  const perPage = params.perPage ?? 15
  /** Show page numbers / per-page when we have data or server reported multiple pages. */
  const showNumericPagination =
    !loading && (pagination.total > 0 || notes.length > 0 || lastPage > 1)

  const openAdd = () => {
    setEditTarget(null)
    setEditOpen(true)
  }

  const openEdit = (note: ReleaseNote) => {
    setEditTarget(note)
    setEditOpen(true)
  }

  const handleSubmit = async (payload: ReleaseNoteSubmitPayload, id?: number) => {
    setSubmitting(true)
    try {
      await saveReleaseNote(payload, id)
      showSuccess('Data Updated Successfully')
      setEditOpen(false)
      setEditTarget(null)
      void load(params)
    } catch (error) {
      showApiError(error)
    } finally {
      setSubmitting(false)
    }
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteReleaseNote(deleteTarget.id)
      showSuccess('Whats New Detail Deleted Successfully!')
      setDeleteTarget(null)
      void load(params)
    } catch (error) {
      showApiError(error)
    }
  }

  const SortHeader = ({ column, label, className }: { column: ReleaseNoteSortKey; label: string; className?: string }) => {
    const isActive = params.orderBy === column
    return (
      <th
        className={cn("select-none cursor-pointer !text-left", className)}
        onClick={() => toggleSort(column)}
      >
        <button
          type="button"
          className={cn(parityStyles.sortHeaderButton, configSortStyles.propertySortHeaderButton, 'justify-start')}
          onClick={(event) => {
            event.stopPropagation()
            toggleSort(column)
          }}
        >
          <span className={parityStyles.sortHeaderLabel}>{label}</span>
          <span
            aria-hidden="true"
            className={cn(parityStyles.sortIcon, isActive && parityStyles.sortIconActive)}
          >
            {isActive ? (params.sortBy === 'asc' ? '▲' : '▼') : <ChevronsUpDown className="h-3 w-3" />}
          </span>
        </button>
      </th>
    )
  }

  const addButton = isAdmin ? (
    <button
      type="button"
      onClick={openAdd}
      className={cn(primaryEntityActionButtonClass, 'w-full sm:w-auto')}
      data-testid="whats-new-add-button"
    >
      Add
    </button>
  ) : undefined

  const colSpanCount = isAdmin ? 4 : 3
  const colWidthClass = isAdmin ? 'w-1/4' : 'w-1/3'

  return (
    <PageShell title="WHAT'S NEW" headerAction={addButton}>
      <ListingDataTableCard>
        {showNumericPagination && (
          <ListPaginationBar
            placement="header"
            currentPage={currentPage}
            lastPage={lastPage}
            totalRecords={pagination.total}
            perPage={perPage}
            onPageChange={goToPage}
            onPerPageChange={changePerPage}
            perPageOptions={PER_PAGE_OPTIONS}
            testIdPrefix={PAGINATION_TEST_ID_PREFIX}
            barDataTestId="whats-new-pagination-header-bar"
          />
        )}

        <div className="w-full">
          <table className={cn(parityStyles.propertyListingTable, parityStyles.listingTable, LISTING_DATA_TABLE_CLASS)}>
            <thead className="border-b border-[#c8d7e8] bg-[#dfeaf5] text-slate-900">
              <tr>
                <SortHeader column="version_date" label="Date" className={colWidthClass} />
                <SortHeader column="version_type" label="Type" className={colWidthClass} />
                <SortHeader column="version_title" label="Title" className={colWidthClass} />
                {isAdmin && (
                  <th className="!whitespace-nowrap px-4 py-3 !text-center w-1/4">
                    Action
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#dfe7f0] [&>tr:nth-child(even)]:bg-[#f2f7fd]">
              {loading ? (
                <ListingTableLoadingRow colSpan={colSpanCount} />
              ) : notes.length === 0 ? (
                <tr>
                  <td colSpan={colSpanCount} className="px-3 py-6 text-center text-sm text-slate-500 sm:px-4 sm:py-8">
                    No release notes yet.
                  </td>
                </tr>
              ) : (
                notes.map((note) => (
                  <tr key={note.id} className={parityStyles.listingTableRow}>
                    <td className="whitespace-nowrap px-2 py-2 text-xs text-slate-700 sm:px-4 sm:py-3 sm:text-sm">
                      {formatDate(note.version_date)}
                    </td>
                    <td
                      className="px-2 py-2 text-sm text-slate-700 sm:px-4 sm:py-3 sm:text-base"
                      title={VERSION_TYPES[note.version_type]}
                    >
                      <span className="line-clamp-2">{VERSION_TYPES[note.version_type]}</span>
                    </td>
                    <td className="min-w-0 px-2 py-2 sm:px-4 sm:py-3">
                      <div
                        className="line-clamp-2 break-words font-medium text-slate-900 [overflow-wrap:anywhere]"
                        title={note.version_title}
                      >
                        {note.version_title}
                      </div>
                    </td>
                    {isAdmin && (
                      <td className="!whitespace-nowrap px-2 py-2 sm:px-4 sm:py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            type="button"
                            onClick={() => openEdit(note)}
                            className="rounded p-1.5 text-[#1597ff] hover:bg-[#1597ff]/10"
                            aria-label="Edit"
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteTarget(note)}
                            className="rounded p-1.5 text-red-600 hover:bg-red-50"
                            aria-label="Delete"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {showNumericPagination && (
          <ListPaginationBar
            placement="footer"
            currentPage={currentPage}
            lastPage={lastPage}
            totalRecords={pagination.total}
            perPage={perPage}
            onPageChange={goToPage}
            onPerPageChange={changePerPage}
            perPageOptions={PER_PAGE_OPTIONS}
            testIdPrefix={PAGINATION_TEST_ID_PREFIX}
            barDataTestId="whats-new-pagination-footer-bar"
          />
        )}
      </ListingDataTableCard>

      <WhatsNewEditModal
        isOpen={editOpen}
        note={editTarget}
        submitting={submitting}
        onClose={() => {
          setEditOpen(false)
          setEditTarget(null)
        }}
        onSubmit={handleSubmit}
      />

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title="Delete Release Note"
        message="Do You Want To Delete this Whats New Detail ?"
        primaryLabel="Delete"
        onPrimary={confirmDelete}
        secondaryLabel="Cancel"
        onSecondary={() => setDeleteTarget(null)}
        variant="warning"
      />
    </PageShell>
  )
}
